import {
  InvoiceStatus,
  ModelSortDirection,
  PaymentFrequency,
  PaymentTerms,
  SearchTasksSortBy,
  Task,
  TaskSearchType,
  TaskType,
  TemplateType,
  DocumentType,
  getTemplate,
} from '@admiin-com/ds-graphql';
import { gql, useQuery } from '@apollo/client';
import { DateTime } from 'luxon';
import XModal from '../../../../components/XModal';
/**
 * DocumentRenderer applies shared document-content styles from
 * features/components/DocumentEditor/document-content.css to ensure
 * WYSIWYG consistency between the preview and the editor.
 */
import DocumentRenderer from '../../../../components/DocumentRenderer/DocumentRenderer';
import {
  WBAutocomplete,
  WBBox,
  WBFlex,
  WBTextFieldV2,
} from '@admiin-com/ds-web';
import {
  BillCreateFormData,
  BillCreateFormDataDefaultValues,
} from '../../../providers/TaskCreationProvider/TaskCreationProvider';
import { useSelectedEntity } from '../../../../hooks/useSelectedEntity/useSelectedEntity';
import useSearchTasks from '../../../hooks/useTasksFromUrl/useSearchTasks';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDocumentPreview } from '../../DocumentCreate/useDocumentPreview/useDocumentPreview';

export const defaultTaskFormValue = {
  ...BillCreateFormDataDefaultValues,
  to: {
    name: 'Coastal Constructions Pty Ltd',
    type: 'COMPANY',
    email: 'accounts@coastalconstructions.com.au',
    phone: '03 8765 4321',
    companyNumber: 'ACN 123 456 789',
    contact: {
      firstName: 'Sarah',
      lastName: 'Johnson',
      email: 'sarah.johnson@coastalconstructions.com.au',
      phone: '0434 567 890',
      website: 'https://coastalconstructions.com.au',
    },
    address: {
      address1: '88 Collins Street',
      address2: 'Level 3 Suite 305',
      city: 'Melbourne',
      state: 'Victoria',
      stateCode: 'VIC',
      postalCode: '3000',
      country: 'Australia',
    },
    partner: {
      firstName: 'John',
      lastName: 'Citizen',
      email: 'john.citizen@outbackbloom.com.au',
      phone: '0487 654 321',
    },
  },
  partner: {
    firstName: 'John',
    lastName: 'Citizen',
    email: 'john.citizen@outbackbloom.com.au',
    phone: '0487 654 321',
  },
  type: TaskType.PAY_ONLY,
  paymentFrequency: PaymentFrequency.ONCE,
  amount: '1500.00',
  dueAt: DateTime.local().plus({ days: 14 }).toISODate(),
  reference: 'INV-2025-001',
  notes:
    'Professional accounting services for monthly bookkeeping and financial reporting. Includes GST reconciliation and BAS preparation.',
  lodgementAt: DateTime.local().toISODate(),
  annotations: null,
  gstInclusive: true,
  numberOfPayments: 1,
  paymentAt: '',
  firstPaymentAt: '',
  invoiceStatus: InvoiceStatus.INVOICE,
  supportDocuments: [],
  sendSMS: false,
  sendEmail: true,
  automaticGenerateInvoice: false,
  automaticInvoicePaymentAt: '',
  automaticInvoicePaymentTerms: PaymentTerms.FORTNIGHT,
  quoteRequiresSignature: false,
  issuedAt: DateTime.local().toISODate(),
  dueTitle: 'Due On',
  documentTitle: 'TAX INVOICE',
  signatureRequired: false,
  frequencyPeriod: '',
  period: '',
  settlementStatus: 'Payable',
};

export const defaultTaxBindingTasks = [
  {
    to: { name: 'Coastal Constructions Pty Ltd', entityType: 'Company' },
    toEntity: { name: 'Coastal Constructions Pty Ltd' },
    entityType: 'Company',
    settlementStatus: 'Payable',
    amount: '1500.00',
    dueAt: DateTime.local().plus({ days: 14 }).toISODate(),
  },
  {
    to: { name: 'Blue Ocean Trust', entityType: 'Trust' },
    toEntity: { name: 'Blue Ocean Trust' },
    entityType: 'Trust',
    settlementStatus: 'Refundable',
    amount: '-300.00',
    dueAt: DateTime.local().plus({ days: 29 }).toISODate(),
  },
  {
    to: { name: 'Lisa Johnson', entityType: 'Individual' },
    toEntity: { name: 'Lisa Johnson' },
    entityType: 'Individual',
    settlementStatus: 'Nil',
    amount: '0.00',
    dueAt: DateTime.local().plus({ days: 54 }).toISODate(),
  },
];

export const defaultTaxBindingFormValue = {
  ...defaultTaskFormValue,
  documentTitle: 'TAX BINDING AGREEMENT',
  tasks: [...defaultTaxBindingTasks],
};

const previewFallbackValues = {
  task: {
    amount: '$1,500.00',
    reference: 'INV-2026-014',
    dueAt: DateTime.local().plus({ days: 14 }).toFormat('dd LLL yyyy'),
    notes:
      'Monthly bookkeeping, reconciliations, BAS preparation, and advisory support.',
    invoiceStatus: 'Invoice',
    dueTitle: 'Due On',
    issuedAt: DateTime.local().toFormat('dd LLL yyyy'),
    documentTitle: 'TAX INVOICE',
    signatureRequired: 'No',
    frequencyPeriod: 'One-off',
    period: 'Jul 2025 - Jun 2026',
    settlementStatus: 'Payable',
    year: DateTime.local().toFormat('yyyy'),
  },
  from: {
    name: 'Harbor Ledger Advisory Pty Ltd',
    firstName: 'Olivia',
    lastName: 'Mason',
    type: 'COMPANY',
    taxNumber: 'ABN 86 123 456 789',
    email: 'accounts@harborledger.com.au',
    phone: '03 9123 4567',
    website: 'https://harborledger.com.au',
    brandColor: '#0E6A56',
    address: {
      address1: '120 Queen Street',
      address2: 'Level 12',
      city: 'Melbourne',
      state: 'Victoria',
      postalCode: '3000',
      country: 'Australia',
    },
    contact: {
      firstName: 'Olivia',
      lastName: 'Mason',
      email: 'olivia.mason@harborledger.com.au',
      phone: '0400 111 222',
    },
  },
  to: {
    name: 'Coastal Constructions Pty Ltd',
    firstName: 'Sarah',
    lastName: 'Johnson',
    type: 'COMPANY',
    taxNumber: 'ABN 11 222 333 444',
    email: 'accounts@coastalconstructions.com.au',
    phone: '03 8765 4321',
    address: {
      address1: '88 Collins Street',
      address2: 'Level 3 Suite 305',
      city: 'Melbourne',
      state: 'Victoria',
      postalCode: '3000',
      country: 'Australia',
    },
    contact: {
      firstName: 'Sarah',
      lastName: 'Johnson',
      email: 'sarah.johnson@coastalconstructions.com.au',
      phone: '0434 567 890',
    },
  },
  partner: {
    firstName: 'John',
    lastName: 'Citizen',
    email: 'john.citizen@harborledger.com.au',
    phone: '0487 654 321',
  },
  dates: {
    today: DateTime.local().toFormat('dd/MM/yyyy'),
    year: DateTime.local().toFormat('yyyy'),
    month: DateTime.local().toFormat('LLLL'),
  },
};

const isObjectRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const fillWithFallback = <T,>(
  currentValue: T,
  fallbackValue: unknown,
  isMissing: (value: unknown) => boolean
): T => {
  if (isMissing(currentValue)) {
    return fallbackValue as T;
  }

  if (Array.isArray(fallbackValue)) {
    if (Array.isArray(currentValue) && currentValue.length > 0) {
      return currentValue;
    }
    return fallbackValue as T;
  }

  if (!isObjectRecord(fallbackValue) || !isObjectRecord(currentValue)) {
    return currentValue;
  }

  const merged: Record<string, unknown> = { ...currentValue };

  for (const [key, fallbackChild] of Object.entries(fallbackValue)) {
    merged[key] = fillWithFallback(merged[key], fallbackChild, isMissing);
  }

  return merged as T;
};

const fillMissingWithFallback = <T,>(
  currentValue: T,
  fallbackValue: unknown
): T =>
  fillWithFallback(
    currentValue,
    fallbackValue,
    (value) => value === undefined || value === null || value === ''
  );

const fillUndefinedWithFallback = <T,>(
  currentValue: T,
  fallbackValue: unknown
): T =>
  fillWithFallback(currentValue, fallbackValue, (value) => value === undefined);

const hasMeaningfulText = (value: string | null | undefined): boolean =>
  Boolean(value?.replace(/\u00a0/g, '').trim());

const isWhitespaceNode = (node: ChildNode): boolean =>
  node.nodeType === Node.TEXT_NODE && !hasMeaningfulText(node.textContent);

const isEmptyBoundaryNode = (node: ChildNode): boolean => {
  if (node.nodeType === Node.COMMENT_NODE || isWhitespaceNode(node)) {
    return true;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }

  const element = node as HTMLElement;
  const tag = element.tagName.toLowerCase();

  if (tag === 'br') {
    return true;
  }

  return Array.from(element.childNodes).every((child) =>
    isEmptyBoundaryNode(child)
  );
};

const hasOnlyOneElementChild = (container: HTMLElement): boolean => {
  const meaningfulNodes = Array.from(container.childNodes).filter(
    (node) =>
      !(
        node.nodeType === Node.COMMENT_NODE ||
        isWhitespaceNode(node as ChildNode)
      )
  );
  return (
    meaningfulNodes.length === 1 &&
    meaningfulNodes[0].nodeType === Node.ELEMENT_NODE
  );
};

const getInnermostSingleWrapper = (container: HTMLElement): HTMLElement => {
  let current = container;
  while (hasOnlyOneElementChild(current)) {
    const child = Array.from(current.childNodes).find(
      (node) => node.nodeType === Node.ELEMENT_NODE
    ) as HTMLElement | undefined;
    if (!child || child.tagName.toLowerCase() !== 'div') {
      break;
    }
    current = child;
  }
  return current;
};

const trimBoundaryEmptyNodes = (
  html: string,
  side: 'start' | 'end'
): string => {
  if (!html.trim() || typeof DOMParser === 'undefined') {
    return html;
  }

  const parser = new DOMParser();
  const document = parser.parseFromString(
    `<div id="boundary-root">${html}</div>`,
    'text/html'
  );
  const root = document.getElementById('boundary-root');

  if (!root) {
    return html;
  }

  const container = getInnermostSingleWrapper(root);
  const getBoundaryNode = () =>
    side === 'start' ? container.firstChild : container.lastChild;

  let boundaryNode = getBoundaryNode();
  while (boundaryNode && isEmptyBoundaryNode(boundaryNode as ChildNode)) {
    boundaryNode.remove();
    boundaryNode = getBoundaryNode();
  }

  return root.innerHTML;
};

export const mergeLetterheadAndTemplateHtml = (
  letterheadHtml: string,
  templateHtml: string
): string => {
  if (!letterheadHtml) return templateHtml;
  if (!templateHtml) return letterheadHtml;

  const normalizedLetterhead = trimBoundaryEmptyNodes(letterheadHtml, 'end');
  const normalizedTemplate = trimBoundaryEmptyNodes(templateHtml, 'start');
  return `${normalizedLetterhead}${normalizedTemplate}`;
};

interface DocumentPreviewModalProps {
  open: boolean;
  onClose: () => void;
  templateHtml?: string | null;
  taskCreationContext?: BillCreateFormData;
  includeLetterhead?: boolean | null | undefined;
  templateType?: TemplateType;
}

export function DocumentPreviewModal({
  open,
  onClose,
  templateHtml,
  taskCreationContext,
  includeLetterhead,
  templateType,
}: DocumentPreviewModalProps) {
  const { t } = useTranslation();
  const { entity } = useSelectedEntity();

  const { data: letterheadData } = useQuery(gql(getTemplate), {
    variables: { id: `${entity?.id}_LETTERHEAD` },
    skip:
      !open ||
      !includeLetterhead ||
      !entity?.id ||
      templateType === TemplateType.LETTERHEAD,
  });
  const letterheadHtml = letterheadData?.getTemplate?.templateHtml ?? '';

  const hideTaskSelect =
    templateType === TemplateType.TAX_BINDING || !!taskCreationContext;

  const isTaxBinding = templateType === TemplateType.TAX_BINDING;

  const previewContext = useMemo(() => {
    if (!isTaxBinding) {
      return taskCreationContext;
    }

    const defaultContext = {
      ...defaultTaxBindingFormValue,
      from: {},
    } as unknown as BillCreateFormData;
    const fallbackPartner = defaultTaxBindingFormValue.partner;

    if (!taskCreationContext) {
      return defaultContext;
    }

    const contextPartner = (
      taskCreationContext as unknown as {
        partner?: typeof fallbackPartner;
      }
    ).partner;

    const hasToDetails = Boolean(
      taskCreationContext.to &&
        (taskCreationContext.to.name ||
          taskCreationContext.to.email ||
          taskCreationContext.to.phone ||
          taskCreationContext.to.companyNumber ||
          taskCreationContext.to.contact?.firstName ||
          taskCreationContext.to.contact?.lastName)
    );

    const hasPartnerDetails = Boolean(
      contextPartner &&
        (contextPartner.firstName ||
          contextPartner.lastName ||
          contextPartner.email ||
          contextPartner.phone)
    );

    const tasks =
      Array.isArray(taskCreationContext.tasks) &&
      taskCreationContext.tasks.length > 0
        ? taskCreationContext.tasks
        : defaultContext.tasks;

    return {
      ...defaultContext,
      ...taskCreationContext,
      to: hasToDetails ? taskCreationContext.to : defaultContext.to,
      partner: hasPartnerDetails ? contextPartner : fallbackPartner,
      tasks,
    } as BillCreateFormData;
  }, [isTaxBinding, taskCreationContext]);

  const { tasks } = useSearchTasks({
    firmId: entity?.id,
    type: TaskSearchType.ALL,
    limit: 7,
    disableAutoFetch: !open || hideTaskSelect,
    sortBy: SearchTasksSortBy.createdAt,
    sortDirection: ModelSortDirection.DESC,
  });

  const filteredTasks = useMemo(() => {
    if (!tasks || templateType === TemplateType.CUSTOM || hideTaskSelect) {
      return tasks;
    }

    const typeMatch = Object.values(DocumentType).includes(
      templateType as unknown as DocumentType
    )
      ? (templateType as unknown as DocumentType)
      : null;

    return typeMatch
      ? tasks.filter((t: any) => t.documentType === typeMatch)
      : tasks;
  }, [tasks, templateType, hideTaskSelect]);

  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const previewValues = useDocumentPreview(
    hideTaskSelect ? null : selectedTask,
    previewContext
  );

  const placeholderDataUrl = useMemo(() => {
    const text = t('signaturePreviewPlaceholder', {
      ns: 'documents',
      defaultValue: 'Signature will be generated',
    });
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="60"><rect width="100%" height="100%" fill="white" stroke="#999" stroke-dasharray="4"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" font-family="Arial" font-size="14" fill="#999">${text}</text></svg>`;
    return `data:image/svg+xml;base64,${btoa(
      unescape(encodeURIComponent(svg))
    )}`;
  }, [t]);

  const previewValuesWithPlaceholder = useMemo(() => {
    if (!previewValues) return null;

    const providedTasks = Array.isArray(previewValues.tasks)
      ? previewValues.tasks
      : Array.isArray(previewValues.task?.tasks)
      ? previewValues.task.tasks
      : undefined;

    const hasProvidedTasks =
      Array.isArray(providedTasks) && providedTasks.length > 0;
    const shouldUseDefaultTasks =
      templateType === TemplateType.TAX_BINDING && !hasProvidedTasks;

    let base = previewValues;

    if (hasProvidedTasks && previewValues.tasks !== providedTasks) {
      base = { ...base, tasks: providedTasks };
    } else if (shouldUseDefaultTasks) {
      base = { ...base, tasks: defaultTaxBindingTasks };
    }

    const baseWithFallbacks = {
      ...base,
      task: fillMissingWithFallback(base.task, previewFallbackValues.task),
      from: fillUndefinedWithFallback(base.from, previewFallbackValues.from),
      to: fillUndefinedWithFallback(base.to, previewFallbackValues.to),
      partner: fillUndefinedWithFallback(
        base.partner,
        previewFallbackValues.partner
      ),
      dates: fillMissingWithFallback(base.dates, previewFallbackValues.dates),
    };

    if (!templateHtml?.includes('{{partner.signatureUrl}}')) {
      return baseWithFallbacks;
    }
    return {
      ...baseWithFallbacks,
      partner: {
        ...baseWithFallbacks.partner,
        signatureUrl: placeholderDataUrl,
      },
    };
  }, [previewValues, templateHtml, placeholderDataUrl, templateType]);

  const combinedHtml = useMemo(() => {
    if (
      !includeLetterhead ||
      !letterheadHtml ||
      templateType === TemplateType.LETTERHEAD
    ) {
      return templateHtml ?? undefined;
    }

    return mergeLetterheadAndTemplateHtml(
      letterheadHtml ?? '',
      templateHtml ?? ''
    );
  }, [includeLetterhead, letterheadHtml, templateHtml, templateType]);

  return (
    <XModal maxWidth="md" open={open} onClose={onClose}>
      <WBFlex flexDirection="column" sx={{ width: '100%' }}>
        {!hideTaskSelect && (
          <WBBox sx={{ width: '100%', padding: '16px 16px 8px 16px' }}>
            <WBAutocomplete
              options={filteredTasks}
              getOptionLabel={(option) =>
                (option as Task).reference || (option as Task).id
              }
              value={selectedTask}
              onChange={(_, v) => setSelectedTask(v as Task)}
              renderInput={(params) => (
                <WBTextFieldV2
                  {...params}
                  label={t('selectRecentTask', {
                    ns: 'documents',
                    defaultValue: 'Preview with a recent task',
                  })}
                />
              )}
            />
          </WBBox>
        )}
        {previewValuesWithPlaceholder && (
          <DocumentRenderer
            values={previewValuesWithPlaceholder}
            customHtml={combinedHtml}
          />
        )}
      </WBFlex>
    </XModal>
  );
}
